import { z } from 'zod';
import { EngagementSettings, TaskBeneficiary, TaskRuleConditions } from './engagement.js';
export const UpsertEngagementSettingsRequest = z.object({
    enabled: z.boolean(),
    poolId: z.string().uuid().optional(),
    poolName: z.string().min(1).max(120).optional(),
    label: z.string().min(1).max(32).optional(),
    shareMode: z.enum(['group', 'shared']).optional(),
    settings: EngagementSettings.optional(),
});
export const CreateTaskRuleRequest = z.object({
    eventKind: z.string().min(1).max(120),
    category: z.string().min(1).max(64).optional(),
    points: z.number().int().min(1).max(100000),
    dailyCap: z.number().int().min(0).max(1000000).nullable().optional(),
    beneficiary: TaskBeneficiary.optional(),
    requireReview: z.boolean().optional(),
    minReviewPoints: z.number().int().min(0).max(100000).optional(),
    conditions: TaskRuleConditions.optional(),
    riskConfig: z.record(z.string(), z.unknown()).optional(),
});
export const CreateTaskRequest = z.object({
    title: z.string().min(1).max(160),
    description: z.string().max(2000).optional(),
    status: z.enum(['draft', 'active', 'paused']).optional(),
    startsAt: z.string().datetime({ offset: true }).nullable().optional(),
    endsAt: z.string().datetime({ offset: true }).nullable().optional(),
    reviewRequired: z.boolean().optional(),
    rules: z.array(CreateTaskRuleRequest).min(1).max(20),
});
export const UpdateTaskRequest = z.object({
    title: z.string().min(1).max(160).optional(),
    description: z.string().max(2000).optional(),
    status: z.enum(['draft', 'active', 'paused', 'archived']).optional(),
    startsAt: z.string().datetime({ offset: true }).nullable().optional(),
    endsAt: z.string().datetime({ offset: true }).nullable().optional(),
    reviewRequired: z.boolean().optional(),
    rules: z.array(CreateTaskRuleRequest).min(1).max(20).optional(),
});
export const ListPointLedgerQuery = z.object({
    userId: z.string().uuid().optional(),
    limit: z.number().int().min(1).max(100).optional(),
    offset: z.number().int().min(0).optional(),
});
export const CreateRewardRequest = z.object({
    title: z.string().min(1).max(160),
    description: z.string().max(2000).optional(),
    status: z.enum(['draft', 'preview', 'open', 'closed']).optional(),
    cost: z.number().int().min(1).max(1000000),
    totalStock: z.number().int().min(0).max(1000000).nullable().optional(),
    perUserLimit: z.number().int().min(1).max(1000000).nullable().optional(),
    requiresApproval: z.boolean().optional(),
    fulfillmentKind: z.string().min(1).max(64).optional(),
    fulfillmentInstructions: z.string().max(4000).nullable().optional(),
    previewStartsAt: z.string().datetime({ offset: true }).nullable().optional(),
    opensAt: z.string().datetime({ offset: true }).nullable().optional(),
    closesAt: z.string().datetime({ offset: true }).nullable().optional(),
    redemptionEndsAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export const UpdateRewardRequest = CreateRewardRequest.partial().extend({
    status: z.enum(['draft', 'preview', 'open', 'closed', 'archived']).optional(),
});
export const RedeemRewardRequest = z.object({
    quantity: z.number().int().min(1).max(100).optional(),
});
export const ReviewDecisionRequest = z.object({
    note: z.string().max(1000).optional(),
});
export const RedemptionDecisionRequest = z.object({
    note: z.string().max(1000).optional(),
});
const NonZeroPointAmount = z.number().int().min(-1000000).max(1000000).refine((value) => value !== 0);
export const ManualPointAdjustmentItemRequest = z.object({
    userId: z.string().uuid(),
    amount: NonZeroPointAmount.optional(),
    category: z.string().min(1).max(64).optional(),
    reason: z.string().min(1).max(300).optional(),
    requireReview: z.boolean().optional(),
});
export const ManualPointAdjustmentRequest = z.object({
    userId: z.string().uuid().optional(),
    amount: NonZeroPointAmount.optional(),
    category: z.string().min(1).max(64).optional(),
    reason: z.string().min(1).max(300).optional(),
    requireReview: z.boolean().optional(),
    adjustments: z.array(ManualPointAdjustmentItemRequest).min(1).max(100).optional(),
}).superRefine((value, ctx) => {
    if (value.adjustments !== undefined) {
        for (const [index, adjustment] of value.adjustments.entries()) {
            if (adjustment.amount === undefined && value.amount === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Each batch adjustment needs an amount or a top-level amount.',
                    path: ['adjustments', index, 'amount'],
                });
            }
            if (adjustment.reason === undefined && value.reason === undefined) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Each batch adjustment needs a reason or a top-level reason.',
                    path: ['adjustments', index, 'reason'],
                });
            }
        }
        return;
    }
    if (value.userId === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'userId is required for a single adjustment.', path: ['userId'] });
    }
    if (value.amount === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'amount is required for a single adjustment.', path: ['amount'] });
    }
    if (value.reason === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'reason is required for a single adjustment.', path: ['reason'] });
    }
});
export const RequestPointPoolLinkRequest = z.object({
    poolId: z.string().uuid(),
    note: z.string().max(1000).optional(),
});
export const DecidePointPoolLinkRequest = z.object({
    note: z.string().max(1000).optional(),
});
//# sourceMappingURL=requests.js.map